import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { PRODUCTS, CATEGORIES, INTENTIONS } from '../mock';
import { fetchProducts } from '../api';
import ProductCard from '../components/ProductCard';

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const [catalog, setCatalog] = useState(PRODUCTS);
  useEffect(() => {
    fetchProducts().then((api) => {
      if (!Array.isArray(api)) return;
      const ids = new Set(PRODUCTS.map((p) => p.id));
      const extra = api.filter((a) => !ids.has(a.id));
      if (extra.length) setCatalog([...PRODUCTS, ...extra]);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    const ref = params.get('ref');
    if (ref) localStorage.setItem('pe_ref', ref);
  }, [params]);
  const [active, setActive] = useState(params.get('category') || 'all');
  const [intention, setIntention] = useState(params.get('intention') || '');
  const [sort, setSort] = useState('featured');
  const [query, setQuery] = useState(params.get('q') || '');

  // Keep filters in sync when the URL params change (e.g. header search submit while already on /shop)
  useEffect(() => {
    setActive(params.get('category') || 'all');
    setIntention(params.get('intention') || '');
    setQuery(params.get('q') || '');
  }, [params]);

  const list = useMemo(() => {
    let l = [...catalog];
    if (active !== 'all') l = l.filter((p) => p.category === active);
    if (intention) l = l.filter((p) => (p.intentions || []).includes(intention));
    if (query.trim()) {
      const words = query.toLowerCase().trim().split(/\s+/);
      const hayOf = (p) => [p.name, p.summary, p.tagline, p.description, p.category, p.form].filter(Boolean).join(' ').toLowerCase();
      let matched = l.filter((p) => words.every((w) => hayOf(p).includes(w)));
      if (matched.length === 0) matched = l.filter((p) => words.some((w) => hayOf(p).includes(w)));
      l = matched;
    }
    l.sort((a, b) => {
      if (sort === 'low') return (a.price ?? 1e9) - (b.price ?? 1e9);
      if (sort === 'high') return (b.price ?? -1) - (a.price ?? -1);
      return (b.bestseller ? 1 : 0) - (a.bestseller ? 1 : 0);
    });
    return l;
  }, [active, intention, sort, query, catalog]);

  const setCat = (id) => { setActive(id); setParams((p) => { p.set('category', id); return p; }); };

  return (
    <div className="pt-28 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-5">
        <div className="text-center mb-4">
          <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">The Apothecary</p>
          <h1 className="font-cinzel text-4xl md:text-6xl text-gold-gradient mb-3">Shop Prestige</h1>
          <div className="gold-divider mb-4" />
          <p className="font-cormorant text-xl text-[#f5ecd9]/60 max-w-2xl mx-auto">Pure plant extracts, essential oils, healing crystals and herbs — crafted to leave you drenched in the beauty of nature.</p>
        </div>

        <div className="max-w-md mx-auto mt-8 mb-8">
          <div className="flex items-center gap-2 bg-white/5 border border-[#d4af37]/30 rounded-full px-4 focus-within:border-[#ff4da6] transition-colors">
            <Search className="h-4 w-4 text-[#f0d582]" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products…" className="flex-1 bg-transparent py-3 text-sm text-[#f5ecd9] placeholder:text-[#f5ecd9]/40 focus:outline-none" />
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2 justify-center">
            {CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-jost transition-all duration-300 ${active === c.id ? 'bg-gradient-to-r from-[#d6197d] to-[#ff4da6] text-white' : 'border border-[#d4af37]/30 text-[#f5ecd9]/70 hover:border-[#ff4da6]/60 hover:text-[#f0d582]'}`}>{c.label}</button>
            ))}
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-[#120d10] border border-[#d4af37]/30 rounded-md px-4 py-2 text-xs text-[#f5ecd9] font-jost focus:outline-none focus:border-[#ff4da6]">
            <option value="featured">Featured</option>
            <option value="low">Price: Low to High</option>
            <option value="high">Price: High to Low</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-2 justify-center mb-10">
          <span className="text-xs uppercase tracking-widest text-[#f5ecd9]/40 font-jost self-center mr-1">Intention:</span>
          <button onClick={() => setIntention('')} className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-jost transition-all ${!intention ? 'bg-[#d4af37]/20 text-[#f0d582] border border-[#d4af37]/50' : 'text-[#f5ecd9]/50 border border-transparent hover:text-[#f0d582]'}`}>All</button>
          {INTENTIONS.map((i) => (
            <button key={i.id} onClick={() => setIntention(intention === i.id ? '' : i.id)} className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-jost transition-all ${intention === i.id ? 'bg-[#d4af37]/20 text-[#f0d582] border border-[#d4af37]/50' : 'text-[#f5ecd9]/50 border border-transparent hover:text-[#f0d582]'}`}>{i.label}</button>
          ))}
        </div>

        {list.length === 0 ? (
          <p className="text-center font-cormorant text-2xl text-[#f5ecd9]/50 py-20">No products match your search.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-7">
            {list.map((p) => (<ProductCard key={p.id} product={p} />))}
          </div>
        )}

        <div className="text-center mt-16">
          <p className="font-cormorant text-xl text-[#f5ecd9]/60">Looking for guidance on your energy?</p>
          <Link to="/services" className="inline-block mt-4 text-[#f0d582] border-b border-[#d4af37]/50 pb-1 hover:text-[#ff4da6] transition-colors font-jost text-sm tracking-widest uppercase">Explore Healing & Services</Link>
        </div>
      </div>
    </div>
  );
};

export default Shop;
